import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, Form, Modal, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';
import { FaArrowLeft, FaPlus, FaTrash, FaEdit } from 'react-icons/fa';

const emptyQuestion = {
  question_text: '',
  question_type: 'single_choice',
  marks: 1,
  is_required: true,
  options: [{ option_text: '', is_correct: true }, { option_text: '', is_correct: false }]
};

const AssessmentQuestions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const listPath = location.pathname.startsWith('/organization/')
    ? '/organization/assessments'
    : '/admin/assessments';
  const [assessment, setAssessment] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(emptyQuestion);
  const [saving, setSaving] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);

  const fetchAssessment = async () => {
    try {
      const response = await api.assessments.getById(id);
      setAssessment(response.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load assessment');
    }
  };

  useEffect(() => {
    fetchAssessment();
  }, [id]);

  const hasObjectiveType = useMemo(
    () => formData.question_type === 'single_choice' || formData.question_type === 'multiple_choice',
    [formData.question_type]
  );

  const handleResetModal = () => {
    setShowModal(false);
    setFormData(emptyQuestion);
    setEditingQuestion(null);
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setFormData({
      question_text: question.question_text || '',
      question_type: question.question_type || 'single_choice',
      marks: Number(question.marks || 1),
      is_required: Boolean(question.is_required),
      options: Array.isArray(question.options) && question.options.length > 0
        ? question.options.map((option) => ({
            option_text: option.option_text || '',
            is_correct: Boolean(option.is_correct)
          }))
        : [{ option_text: '', is_correct: true }, { option_text: '', is_correct: false }]
    });
    setShowModal(true);
  };

  const handleAddOption = () => {
    setFormData((prev) => ({
      ...prev,
      options: [...prev.options, { option_text: '', is_correct: false }]
    }));
  };

  const handleOptionTextChange = (index, value) => {
    setFormData((prev) => ({
      ...prev,
      options: prev.options.map((item, itemIndex) => (
        itemIndex === index ? { ...item, option_text: value } : item
      ))
    }));
  };

  const handleOptionCorrectChange = (index, checked) => {
    setFormData((prev) => {
      if (prev.question_type === 'single_choice') {
        return {
          ...prev,
          options: prev.options.map((item, itemIndex) => ({
            ...item,
            is_correct: itemIndex === index ? checked : false
          }))
        };
      }
      return {
        ...prev,
        options: prev.options.map((item, itemIndex) => (
          itemIndex === index ? { ...item, is_correct: checked } : item
        ))
      };
    });
  };

  const handleRemoveOption = (index) => {
    setFormData((prev) => {
      if (prev.options.length <= 2) {
        return prev;
      }
      return {
        ...prev,
        options: prev.options.filter((_, itemIndex) => itemIndex !== index)
      };
    });
  };

  const validateQuestionForm = () => {
    if (!formData.question_text.trim()) {
      toast.error('Question text is required');
      return false;
    }
    if (!hasObjectiveType) {
      return true;
    }

    const validOptions = formData.options.filter((item) => item.option_text.trim());
    if (validOptions.length < 2) {
      toast.error('At least 2 options are required');
      return false;
    }

    const hasCorrect = validOptions.some((item) => item.is_correct);
    if (!hasCorrect) {
      toast.error('Mark at least 1 correct option');
      return false;
    }

    return true;
  };

  const handleAddQuestion = async () => {
    if (!validateQuestionForm()) {
      return;
    }

    const payload = {
      assessment_id: Number(id),
      question_text: formData.question_text.trim(),
      question_type: formData.question_type,
      marks: Number(formData.marks || 1),
      is_required: Boolean(formData.is_required),
      options: hasObjectiveType
        ? formData.options
            .filter((item) => item.option_text.trim())
            .map((item) => ({
              option_text: item.option_text.trim(),
              is_correct: Boolean(item.is_correct)
            }))
        : []
    };

    try {
      setSaving(true);
      if (editingQuestion) {
        await api.assessments.updateQuestion(editingQuestion.id, payload);
        toast.success('Question updated successfully');
      } else {
        await api.assessments.addQuestion(payload);
        toast.success('Question added successfully');
      }
      handleResetModal();
      fetchAssessment();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add question');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    try {
      await api.assessments.deleteQuestion(questionId);
      toast.success('Question deleted successfully');
      fetchAssessment();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete question');
    }
  };

  return (
    <div className="p-4 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(listPath)}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-purple-300 text-purple-700 rounded-lg shadow-md hover:bg-purple-50 hover:shadow-lg transition-all duration-200 text-sm font-medium"
          >
            <FaArrowLeft className="w-4 h-4" /> Back
          </button>
          <h5 className="mb-0 text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
            Assessment Questions - {assessment?.title || ''}
          </h5>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 py-2 px-8"
        >
          <FaPlus className="w-4 h-4" /> Add Question
        </button>
      </div>

      <div className="bg-white rounded-xl shadow p-4">
        {(assessment?.questions || []).map((question, index) => (
          <div key={question.id} className="border rounded p-3 mb-3">
            <div className="flex justify-between items-center">
              <div>
                <strong>Q{index + 1}:</strong> {question.question_text}
                <div className="text-sm text-gray-600">Type: {question.question_type} | Marks: {question.marks}</div>
              </div>
              <div className="flex items-center gap-2">
                <button className="border-0 bg-transparent text-purple-600 hover:bg-purple-50 rounded p-2" onClick={() => handleEditQuestion(question)} title="Edit Question">
                  <FaEdit />
                </button>
                <button className="border-0 bg-transparent text-red-600 hover:bg-red-50 rounded p-2" onClick={() => handleDeleteQuestion(question.id)} title="Delete Question">
                  <FaTrash />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Modal show={showModal} onHide={handleResetModal} centered>
        <Modal.Header closeButton className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
          <Modal.Title>{editingQuestion ? 'Edit Question' : 'Add Question'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={12}>
              <Form.Label>Question Text</Form.Label>
              <Form.Control value={formData.question_text} onChange={(e) => setFormData((prev) => ({ ...prev, question_text: e.target.value }))} />
            </Col>
            <Col md={6}>
              <Form.Label>Question Type</Form.Label>
              <Form.Select value={formData.question_type} onChange={(e) => setFormData((prev) => ({ ...prev, question_type: e.target.value }))}>
                <option value="single_choice">Single Choice</option>
                <option value="multiple_choice">Multiple Choice</option>
                <option value="subjective">Subjective</option>
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label>Marks</Form.Label>
              <Form.Control type="number" value={formData.marks} onChange={(e) => setFormData((prev) => ({ ...prev, marks: Number(e.target.value) }))} />
            </Col>
            <Col md={12}>
              <Form.Check
                type="checkbox"
                id="assessment-question-required"
                label="This question is required"
                checked={Boolean(formData.is_required)}
                onChange={(e) => setFormData((prev) => ({ ...prev, is_required: e.target.checked }))}
              />
            </Col>

            {hasObjectiveType && (
              <Col md={12}>
                <div className="border rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <Form.Label className="mb-0 font-semibold">Answer Options</Form.Label>
                    <button
                      type="button"
                      onClick={handleAddOption}
                      className="border-1 border-purple-600 bg-white text-purple-600 font-semibold rounded-lg shadow-md transition-all duration-200 text-xs flex items-center gap-2 px-3 py-2"
                    >
                      <FaPlus className="w-3 h-3" /> Add Option
                    </button>
                  </div>

                  <div className="space-y-2">
                    {formData.options.map((option, index) => (
                      <div key={`option-${index}`} className="flex gap-2 items-center">
                        <Form.Control
                          value={option.option_text}
                          placeholder={`Option ${index + 1}`}
                          onChange={(e) => handleOptionTextChange(index, e.target.value)}
                        />
                        <Form.Check
                          type={formData.question_type === 'single_choice' ? 'radio' : 'checkbox'}
                          name="is-correct-option"
                          label="Correct"
                          checked={Boolean(option.is_correct)}
                          onChange={(e) => handleOptionCorrectChange(index, e.target.checked)}
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveOption(index)}
                          disabled={formData.options.length <= 2}
                          className="border-1 border-red-300 text-red-600 rounded p-2 disabled:opacity-40"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </Col>
            )}
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button
            bsPrefix="custom-hamburger"
            onClick={handleResetModal}
            className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
          >
            Cancel
          </Button>
          <Button
            bsPrefix="custom-hamburger"
            onClick={handleAddQuestion}
            disabled={saving}
            className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
          >
            {saving ? 'Saving...' : editingQuestion ? 'Update Question' : 'Save Question'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AssessmentQuestions;
